const SESSION_KEY = 'cl_auth_session';
const TOKEN_COOKIE = 'cl_auth_token';

export interface AuthSession {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  user: {
    id: string;
    email: string;
    displayName: string;
    role: string;
    practiceAreas: string[];
    complianceRoles?: string[];
  };
}

export function getSession(): AuthSession | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const session: AuthSession = JSON.parse(raw);
    if (session.expiresAt < Date.now()) {
      clearSession();
      return null;
    }
    return session;
  } catch {
    return null;
  }
}

// Match cl_session's lifetime (set server-side in apps/api/src/routes/auth.ts).
// The middleware only checks for cl_auth_token's *presence*, not contents — so the
// access token may expire inside the cookie without invalidating the gate; the
// dashboard refreshes silently via cl_session when an API call returns 401.
// Tying cl_auth_token to the Azure access token TTL (~8h) just causes premature
// /login redirects and breaks E2E storageState replay before cl_session expires.
//
// NEXT_PUBLIC_SESSION_COOKIE_MAX_AGE_DAYS is embedded at build time and must
// mirror the API's SESSION_COOKIE_MAX_AGE_DAYS for the deploy. Default 1 day
// = prod posture; dev tier ships with 90 baked into the dashboard image.
const SESSION_COOKIE_MAX_AGE_DAYS =
  parseInt(process.env.NEXT_PUBLIC_SESSION_COOKIE_MAX_AGE_DAYS ?? '1', 10) || 1;
const TOKEN_COOKIE_MAX_AGE_SECONDS = SESSION_COOKIE_MAX_AGE_DAYS * 24 * 60 * 60;

/**
 * `; Secure` whenever the page itself was served over HTTPS (audit2 §14,
 * 2026-10-06). `cl_auth_token` carries the bearer JWT and used to go out
 * without Secure on every tier, so the first plaintext request to the firm
 * host (before HSTS is cached) leaked it. This cookie is written by the
 * browser, so the page's own scheme is the exact truth — no build-time env
 * to keep in sync with `COOKIE_SECURE` on the API; a local http stack keeps
 * working because it is not https.
 */
export function tokenCookieSecureAttr(): string {
  return typeof window !== 'undefined' && window.location.protocol === 'https:' ? '; Secure' : '';
}

export function setSession(session: AuthSession): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  document.cookie = `${TOKEN_COOKIE}=${session.accessToken}; path=/; max-age=${TOKEN_COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${tokenCookieSecureAttr()}`;
}

export function clearSession(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(SESSION_KEY);
  document.cookie = `${TOKEN_COOKIE}=; path=/; max-age=0; SameSite=Lax${tokenCookieSecureAttr()}`;
}

/** How long logout waits for the API before signing out locally anyway. */
const LOGOUT_TIMEOUT_MS = 3000;

/**
 * `POST /api/auth/logout` (audit2 §22, 2026-10-06): the API revokes every
 * live token for the person `accessToken` names and clears `cl_session` +
 * `cl_auth_token`. Relative URL, same origin, so both cookies ride along.
 * Never throws and never waits longer than LOGOUT_TIMEOUT_MS — the caller
 * clears local state next regardless.
 */
export async function revokeServerSession(accessToken: string | null): Promise<void> {
  if (typeof window === 'undefined' || typeof fetch !== 'function') return;
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const timer = setTimeout(() => controller?.abort(), LOGOUT_TIMEOUT_MS);
  try {
    await fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'same-origin',
      keepalive: true,
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      signal: controller?.signal,
    });
  } catch {
    // Network failure or timeout: local sign-out still happens.
  } finally {
    clearTimeout(timer);
  }
}

export function getAccessToken(): string | null {
  const session = getSession();
  return session?.accessToken ?? null;
}

/**
 * First-admin bootstrap (design spec 2026-09-22; fix round 2, Ruling R6-3)
 * — turn a freshly re-minted FULL-session access token into a stored
 * session, the exact same way `/auth/callback` does for the
 * server-redirect login flow
 * (`app/(auth)/auth/callback/page.tsx#fetchUserAndSetSession`): fetch the
 * profile with the NEW token — not whatever `getAccessToken()` still
 * holds — then `setSession()` with it, so `getAccessToken()` and the
 * `cl_auth_token` cookie both flip to the full session immediately and
 * the admin's NEXT request already carries it, no manual re-login.
 *
 * The one caller today is `useVerifyConsent` (`lib/api/admin-workspace.ts`)
 * — `POST /workspace/consent/verify`'s GRANTED response carries
 * `accessToken` only when the caller's session was an onboarding one
 * (`services/auth.ts`'s `onb` claim), mirroring what `GET /callback`
 * already does via its `/auth/callback?token=…` redirect.
 *
 * Best-effort: a failed fetch leaves the existing (still-valid,
 * still-allow-listed onboarding) session in place rather than throwing —
 * the grant itself already succeeded server-side, and a transient
 * profile-fetch failure must not undo that or fail the caller's mutation.
 */
export async function upgradeSessionAccessToken(accessToken: string): Promise<boolean> {
  try {
    const res = await fetch('/api/auth/me', {
      headers: { Authorization: `Bearer ${accessToken}` },
      credentials: 'include',
    });
    if (!res.ok) return false;
    const body = await res.json();

    setSession({
      accessToken,
      refreshToken: '',
      expiresAt: Date.now() + TOKEN_COOKIE_MAX_AGE_SECONDS * 1000,
      user: body.data,
    });
    return true;
  } catch {
    return false;
  }
}
